import React, { useEffect, useMemo, useState, useRef } from 'react';
import ReactCountryFlag from 'react-country-flag';
import { useLocation, useParams } from 'react-router-dom';
import * as pagesApi from '../api/pagesStore';
import { useAuth } from '../contexts/AuthContext';
import * as contApi from '../api/continue';
import { toEmbedAutoplay, toEmbedWithStart } from '../api/filemoon';
import { RxCross2 } from 'react-icons/rx';
import { IoIosArrowBack } from 'react-icons/io';
import { MdOutlineFlag } from 'react-icons/md';
import { MdOutlineSubtitles } from 'react-icons/md';
import { PiCardsLight } from 'react-icons/pi';
import { FaArrowLeftLong } from 'react-icons/fa6';
import { TbPlayerSkipForward } from 'react-icons/tb';
import ReportIssueModal from '../components/ReportIssueModal';
import { FaPlay } from 'react-icons/fa';
import { FaArrowRightLong } from 'react-icons/fa6';
import { IoMdCheckmark } from 'react-icons/io';

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function PlayPage() {
  const params = useParams();
  const q = useQuery();
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [backPid, setBackPid] = useState('');
  const [backKind, setBackKind] = useState(''); // 'serial' | 'film' | ''
  const [headerOffset, setHeaderOffset] = useState(16);
  const [backHover, setBackHover] = useState(false);
  const [reportHover, setReportHover] = useState(false);
  const [isReportOpen, setIsReportOpen] = useState(false);
  const [episodesHover, setEpisodesHover] = useState(false);
  const { user, role } = useAuth();
  const playId = (params.id || '').toString();
  const [popup, setPopup] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentSeason, setCurrentSeason] = useState(1);
  const [expandedEpIdx, setExpandedEpIdx] = useState(null); // index in popup.episodes
  const [hoveredEpIdx, setHoveredEpIdx] = useState(null);
  const [playIconHoverIdx, setPlayIconHoverIdx] = useState(null);
  const [vipBlocked, setVipBlocked] = useState(false);
  const [blockedReason, setBlockedReason] = useState('');
  const [sidebarFading, setSidebarFading] = useState(false);
  const [displaySeason, setDisplaySeason] = useState(1);
  const [sidebarReady, setSidebarReady] = useState(false);
  const [highlightedSeason, setHighlightedSeason] = useState(null);
  const [showSeasonPicker, setShowSeasonPicker] = useState(false);
  const [revealedAllSeasons, setRevealedAllSeasons] = useState(false);
  const [showSeasonsPanel, setShowSeasonsPanel] = useState(false);
  const hoverCloseTimer = useRef(null);
  const [seasonsViewOnly, setSeasonsViewOnly] = useState(false);
  const [seasonEnteringIdx, setSeasonEnteringIdx] = useState(null);
  const sidebarRef = useRef(null);
  const episodeRefs = useRef({});
  const seasonRefs = useRef({});
  const mousePos = useRef({ x: 0, y: 0 });
  const [sidebarNoTransition, setSidebarNoTransition] = useState(false);
  const [langPopupOpen, setLangPopupOpen] = useState(false);
  const langIconRef = useRef(null);
  const [langPopupStyle, setLangPopupStyle] = useState(null);
  const langCloseTimer = useRef(null);
  const [hoveredLangCode, setHoveredLangCode] = useState(null);
  // helper: derive current episode from popup + playId
  const currentEp = (popup && Array.isArray(popup.episodes)) ? popup.episodes.find(e => String(e.playId||'') === String(playId||'')) : null;

  function switchToLanguageVideo(videoUrl) {
    try {
      if (!videoUrl) return;
      const final = toEmbedAutoplay(videoUrl);
      setUrl(final);
      // close language popup after switching
      setLangPopupOpen(false);
    } catch (e) {}
  }
  // layout constants to make width adjustments easier
  const SIDEBAR_WIDTH = 600; // widened slightly more to give extra room for titles
  const SEASONS_PANEL_WIDTH = 320; // increased from 300

  // Clear the entering flag shortly after it's set so the CSS transition can run
  useEffect(() => {
    // track last mouse position so we can detect if pointer is over an episode
    function onMove(e) {
      try { mousePos.current = { x: e.clientX, y: e.clientY }; } catch {}
    }
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  // Clear the entering flag shortly after it's set so the CSS transition can run
  useEffect(() => {
    if (seasonEnteringIdx == null) return;
    const t = setTimeout(() => setSeasonEnteringIdx(null), 30);
    return () => clearTimeout(t);
  }, [seasonEnteringIdx]);

  const [seasonsFadeIn, setSeasonsFadeIn] = useState(false);

  // quick exit animation index when leaving a season to the seasons list
  const [seasonExitingIdx, setSeasonExitingIdx] = useState(null);
  // skip-next small popup states
  const [skipHover, setSkipHover] = useState(false);
  const [skipPlayHover, setSkipPlayHover] = useState(false);
  const skipCloseTimer = useRef(null);

  const nextEp = useMemo(() => {
    try {
      if (!popup || !Array.isArray(popup.episodes)) return null;
      const idx = (popup.episodes || []).findIndex(e => String(e.playId||'') === String(playId||''));
      const ni = idx >= 0 ? idx + 1 : -1;
      if (ni < 0 || ni >= (popup.episodes || []).length) return null;
      const ep = popup.episodes[ni];
      const season = Number(ep.season || 1);
      const epsOfSeason = (popup.episodes || []).filter(e => Number(e.season || 1) === season);
      const idxWithinSeason = Math.max(0, epsOfSeason.indexOf(ep)) + 1;
      return { ep, idx: ni, season, episodeNumberInSeason: idxWithinSeason };
    } catch (e) { return null; }
  }, [popup, playId]);
  // When switching to seasons-only view, run a quick fade-in animation
  useEffect(() => {
    if (!seasonsViewOnly) {
      setSeasonsFadeIn(false);
      return;
    }
    // Start hidden, then clear to animate in
    setSeasonsFadeIn(true);
    requestAnimationFrame(() => requestAnimationFrame(() => setSeasonsFadeIn(false)));
  }, [seasonsViewOnly]);


  // helper to smoothly scroll an episode into view inside the sidebar
  function scrollToEpisodeIdx(globalIdx, instant = false) {
    try {
      const el = episodeRefs.current && episodeRefs.current[globalIdx];
      if (!el) return;
      // prefer smooth scroll into the sidebar container and center the episode when possible
      const behavior = instant ? 'auto' : 'smooth';
      el.scrollIntoView({ behavior, block: 'center', inline: 'nearest' });
    } catch (e) {
      // ignore
    }
  }

  // clear episode refs when popup changes to avoid stale nodes
  useEffect(() => {
    episodeRefs.current = {};
  }, [popup]);

  // center the active episode in the sidebar when popup/playId changes and sidebar is open
  useEffect(() => {
    try {
      if (!popup) return;
      // if the sidebar was just closed, reset temporary selection state
      if (!sidebarOpen) {
        try { setCurrentSeason(1); } catch {}
        try { setSeasonEnteringIdx(null); } catch {}
        try { setExpandedEpIdx(null); } catch {}
        return;
      }
      const idx = (popup.episodes || []).findIndex(e => String(e.playId||'') === String(playId||''));
      if (idx >= 0) {
        try {
          const ep = (popup.episodes || [])[idx];
          if (ep) setCurrentSeason(Number(ep.season || 1));
        } catch {}
        // ensure we open the episodes view (not the seasons-only view) when sidebar opens
        try { setSeasonsViewOnly(false); } catch {}
        // make the initial open instant (no scroll animation and no sidebar slide)
        // briefly disable the sidebar left transition so it mounts already in position
        setSidebarNoTransition(true);
        // wait for next paint so nodes exist, then instantly scroll and re-enable transition
        requestAnimationFrame(() => requestAnimationFrame(() => {
          try { scrollToEpisodeIdx(idx, true); } catch {}
          setTimeout(() => setSidebarNoTransition(false), 40);
        }));
      }
    } catch {}
  }, [popup, playId, sidebarOpen]);

  // Ensure we always center the active episode when the sidebar is opened,
  // even if popup/playId didn't change (covers open -> seasons view -> close -> reopen flows).
  useEffect(() => {
    try {
      if (!sidebarOpen) return;
      if (!popup) return;
      const idx = (popup.episodes || []).findIndex(e => String(e.playId||'') === String(playId||''));
      if (idx < 0) return;

      // Make the open instant and center the episode. If refs aren't available yet
      // (render hasn't produced the nodes), retry a few times shortly after.
  // also ensure we're showing episodes (not the seasons-only panel)
  try { setSeasonsViewOnly(false); } catch {}
  setSidebarNoTransition(true);
  setSidebarReady(false);

      let attempts = 0;
      const tryScroll = () => {
        attempts += 1;
        const el = episodeRefs.current && episodeRefs.current[idx];
        if (el) {
          try { scrollToEpisodeIdx(idx, true); } catch {}
          setTimeout(() => { setSidebarNoTransition(false); setSidebarReady(true); }, 40);
        } else if (attempts < 4) {
          // wait a short bit and try again
          setTimeout(tryScroll, 60);
        } else {
          // give up and re-enable transitions
          setSidebarNoTransition(false);
          setSidebarReady(true);
        }
      };

      // kick off after paint
      requestAnimationFrame(() => requestAnimationFrame(tryScroll));
    } catch (e) {}
  }, [sidebarOpen]);

  // Hide global Bottom footer while on the play page
  useEffect(() => {
    const els = Array.from(document.querySelectorAll('.bodybot'));
    const prev = els.map(el => ({ el, display: el.style.display }));
    els.forEach(el => { try { el.style.display = 'none'; } catch {} });
    return () => {
      prev.forEach(({ el, display }) => { try { el.style.display = display || ''; } catch {} });
    };
  }, []);

  // Measure header height to place the back button just below it
  useEffect(() => {
    const el = document.querySelector('header');
    const h = el && el.offsetHeight ? el.offsetHeight : 8; // when no header on play page, keep button near top
    setHeaderOffset(h + 8);
  }, []);

  // Prevent visible season/content changes during the sidebar fade in/out.
  useEffect(() => {
    let t = null;
    // whenever sidebar toggles, mark fading for the duration of the opacity transition
    // Special-case: when opening the sidebar, immediately show the current season
    // to avoid briefly rendering season 1 and then flashing to the correct one.
    if (sidebarOpen) {
      // ensure the visible season matches the logical one right away
      try { setDisplaySeason(currentSeason); } catch {}
      // don't treat opening as a blocking "fading" window for displaySeason updates
      setSidebarFading(false);
    } else {
      // closing: temporarily mark fading so child transitions don't reflow during opacity
      setSidebarFading(true);
    }

    t = setTimeout(() => {
      setSidebarFading(false);
      // after fade-out completes, mark not-ready so next open pre-scrolls again
      if (!sidebarOpen) setSidebarReady(false);
    }, 120); // match the CSS opacity duration
    return () => { if (t) clearTimeout(t); };
  }, [sidebarOpen]);

  // Keep a separate 'displaySeason' that only updates when not fading
  useEffect(() => {
    if (!sidebarFading) setDisplaySeason(currentSeason);
  }, [currentSeason, sidebarFading]);

  // When the seasons panel/picker is closed (user leaves the seasons UI),
  // restore/lock the visible season to the one that corresponds to the
  // currently playing episode so the sidebar shows the season of the
  // episode you're on instead of the last previewed season.
  const prevShowSeasonsPanel = useRef(showSeasonsPanel);
  const prevSeasonsViewOnly = useRef(seasonsViewOnly);
  useEffect(() => {
    try {
      const closedPanel = prevShowSeasonsPanel.current && !showSeasonsPanel;
      const leftSeasonsView = prevSeasonsViewOnly.current && !seasonsViewOnly;
      if (closedPanel || leftSeasonsView) {
        // compute active season from the currently playing episode (playId)
        let active = currentSeason;
        try {
          const eps = Array.isArray(popup && popup.episodes) ? popup.episodes : [];
          const idx = eps.findIndex(e => String(e.playId || '') === String(playId || ''));
          if (idx >= 0) active = Number(eps[idx].season || active || 1);
        } catch (e) {}
  try { setHighlightedSeason(null); } catch {}
      }
    } finally {
      prevShowSeasonsPanel.current = showSeasonsPanel;
      prevSeasonsViewOnly.current = seasonsViewOnly;
    }
  }, [showSeasonsPanel, seasonsViewOnly, popup, playId, currentSeason]);

  // If the seasons UI is shown and the mouse is already positioned over a
  // season element (element was just rendered under the cursor), detect that
  // and set the highlight so the user sees the hovered state even without a
  // mouseenter event firing.
  useEffect(() => {
    try {
      if (!(showSeasonsPanel || seasonsViewOnly)) return;
      const pos = mousePos.current || {};
      if (typeof pos.x !== 'number') return;
      const elAt = document.elementFromPoint(pos.x, pos.y);
      if (!elAt) return;
      // check all seasonRefs (both picker_ and slide_ keys and numeric keys)
      const keys = Object.keys(seasonRefs.current || {});
      for (let k of keys) {
        const el = seasonRefs.current[k];
        if (!el) continue;
        if (el === elAt || el.contains(elAt) || (elAt.contains && elAt.contains(el))) {
          // derive season index from key if possible, else fall back to null
          const m = k.match(/(\d+)$/);
          const idx = m ? Number(m[1]) : null;
          if (idx != null) {
            try { setHighlightedSeason(idx+1); } catch {}
            return;
          }
        }
      }
      // nothing matched -> clear highlight
      try { setHighlightedSeason(null); } catch {}
    } catch (e) {}
  }, [showSeasonsPanel, seasonsViewOnly, sidebarReady]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const id = params.id || q.get('v') || '';
      let foundUrl = '';
      // reset VIP block state for this load (if hook exists)
      try { setVipBlocked && setVipBlocked(false); } catch (_) {}
        let pageRecord = null;
        let foundPopupId = '';
        if (id) {
          const pages = await pagesApi.getPages();
          const path = `/play/${id}`;
          const it = Array.isArray(pages) ? pages.find(p => p.path === path && !p.disabled) : null;
          if (it) {
            pageRecord = it;
            try {
              const parsed = JSON.parse(it.content || '{}');
              try { window.__play_meta = parsed; } catch {}
              foundUrl = parsed.url || '';
              if (parsed && parsed.popupId) foundPopupId = String(parsed.popupId);
              if (parsed && parsed.showTitle) { /* no-op, keep for future */ }
              if (parsed && parsed.contentType) setBackKind(String(parsed.contentType));
            } catch {
              foundUrl = it.content || '';
            }
          }
        }
        // fallback: direct url from query
        if (!foundUrl) {
          const u = q.get('url');
          if (u) foundUrl = u;
          // Try to reconstruct from popup if popup id present in query
          if (!foundUrl) {
            const qp = q.get('popup');
            if (qp) {
              try {
                const r = await fetch(`/api/popups/${encodeURIComponent(qp)}`, { cache: 'no-store' });
                if (r.ok) {
                  const popup = await r.json();
                // compute remaps between old popup (popup) and new popup (j) and update current user's continueSeries
                try {
                  const oldPopup = popup;
                  if (user && oldPopup && Array.isArray(oldPopup.episodes) && j && Array.isArray(j.episodes)) {
                    const remaps = {};
                    for (const oldEp of oldPopup.episodes) {
                      const match = j.episodes.find(ne => Number(ne.season || 1) === Number(oldEp.season || 1) && Number(ne.index || 0) === Number(oldEp.index || 0) && String((ne.title||'')).trim() === String((oldEp.title||'')).trim());
                      if (match && String(match.playId||'') !== String(oldEp.playId||'')) {
                        remaps[String(oldEp.playId||'')] = String(match.playId||'');
                      }
                    }
                    if (Object.keys(remaps).length) {
                      const list = await contApi.listSeries(user.uid).catch(()=>[]);
                      for (const cont of Array.isArray(list) ? list : []) {
                        try {
                          if (String(cont.popupId||'') === String(pid)) {
                            const newPlay = remaps[String(cont.playId||'')];
                            if (newPlay) {
                              // preserve other metadata when upserting
                              await contApi.upsertSeries(user.uid, pid, {
                                playId: newPlay,
                                path: `/play/${newPlay}`,
                                epTitle: cont.epTitle || '',
                                showTitle: cont.showTitle || '',
                                img: cont.img || '',
                                season: cont.season || null,
                                index: cont.index || null,
                              }).catch(()=>{});
                            }
                          }
                        } catch {}
                      }
                    }
                  }
                } catch {}

                setPopup(j || null);
                  if (ep && ep.videoUrl) foundUrl = toEmbedAutoplay(ep.videoUrl);
                  if (!foundPopupId) foundPopupId = String(popup.id || qp);
                }
              } catch {}
            }
          }
          // Last resort: scan all popups and find episode by playId (works without user or popup query)
          if (!foundUrl && id) {
            try {
              const r = await fetch('/api/popups', { cache: 'no-store' });
              if (r.ok) {
                const all = await r.json();
                outer: for (const p of (Array.isArray(all) ? all : [])) {
                  const eps = Array.isArray(p.episodes) ? p.episodes : [];
                  for (const ep of eps) {
                    if (String(ep.playId||'') === String(id)) {
                      if (ep.videoUrl) {
                        if (ep.vipOnly && !['VIP','Moderátor','Admin','Majitel'].includes(role)) {
                          try { setVipBlocked(true); setBlockedReason('Tato epizoda je pouze pro VIP'); } catch (_) {}
                        } else {
                          foundUrl = toEmbedAutoplay(ep.videoUrl);
                        }
                      }
                      foundPopupId = String(p.id || foundPopupId || '');
                      try { if (p.contentType) setBackKind(String(p.contentType)); } catch {}
                      // Enrich meta and persist to Continue so Home can show it
                      try {
                        const enriched = {
                          popupId: String(p.id || foundPopupId || ''),
                          showTitle: p.title || '',
                          epTitle: ep.title || '',
                          season: ep.season || null,
                          index: ep.index || null,
                          img: p.listThumb || p.searchPoster || p.heroImage || ep.thumb || ''
                        };
                        try { window.__play_meta = enriched; } catch {}
                        if (user && enriched.popupId) {
                          contApi.upsertSeries(user.uid, enriched.popupId, {
                            playId: id,
                            path: `/play/${id}`,
                            epTitle: enriched.epTitle,
                            showTitle: enriched.showTitle,
                            img: enriched.img,
                            season: enriched.season,
                            index: enriched.index,
                          });
                        }
                        // Also patch page content for next time if we have a page record
                        if (pageRecord && pageRecord.path) {
                          try {
                            let parsed = {};
                            try { parsed = JSON.parse(pageRecord.content || '{}'); } catch {}
                            const kind = String(p.contentType || parsed.contentType || '');
                            const nextContent = JSON.stringify({ ...parsed, popupId: enriched.popupId, contentType: kind });
                            await pagesApi.addPage({ title: pageRecord.title, path: pageRecord.path, content: nextContent });
                          } catch {}
                        }
                      } catch {}
                      break outer;
                    }
                  }
                }
              }
            } catch {}
          }
        }
        // If we still don't know popupId, try to infer from user's continueSeries and update the page content
        try {
          if (!foundPopupId) {
            const uid = user?.uid;
            if (uid && id) {
              const list = await contApi.listSeries(uid).catch(()=>[]);
              const hit = Array.isArray(list) ? list.find(x => String(x.playId||'') === String(id)) : null;
              if (hit && hit.popupId) {
                foundPopupId = String(hit.popupId);
                const kind = (hit.contentType === 'film' ? 'film' : hit.contentType === 'serial' ? 'serial' : '');
                if (kind) setBackKind(kind);
                // patch local/remote page content to include popupId for next time
                if (pageRecord && pageRecord.path) {
                  try {
                    let parsed = {};
                    try { parsed = JSON.parse(pageRecord.content || '{}'); } catch {}
                    const nextContent = JSON.stringify({ ...parsed, popupId: foundPopupId, contentType: kind });
                    await pagesApi.addPage({ title: pageRecord.title, path: pageRecord.path, content: nextContent });
                  } catch {}
                }
              }
            }
          }
        } catch {}
        if (mounted) {
          const start = Number(q.get('t') || 0) || 0;
          const finalUrl = start > 0 ? toEmbedWithStart(foundUrl, start) : toEmbedAutoplay(foundUrl);
          setUrl(foundUrl ? finalUrl : '');
          const qp = q.get('popup');
          setBackPid(String(foundPopupId || qp || ''));
        }
        // If play page lacks valid popupId, proactively remove it (auto-clean orphan pages)
        // Do NOT auto-delete pages when popupId missing; keep legacy pages to avoid breaking links across browsers
        // Enrich meta from popup if missing and popup id is provided
        try {
          const meta = window.__play_meta || {};
          const needTitle = !meta.epTitle;
          const needImg = !meta.img;
          const popupQ = q.get('popup');
          if ((needTitle || needImg) && popupQ && id) {
            const r = await fetch(`/api/popups/${encodeURIComponent(popupQ)}`, { cache: 'no-store' });
            if (r.ok) {
              const popup = await r.json();
              const ep = (popup.episodes || []).find(e => String(e.playId||'') === String(id));
              if (ep) {
                const enriched = {
                  ...meta,
                  popupId: popupQ,
                  showTitle: popup.title || meta.showTitle,
                  epTitle: ep.title || meta.epTitle,
                  season: ep.season || meta.season,
                  index: ep.index || meta.index,
                  img: popup.listThumb || popup.searchPoster || popup.heroImage || ep.thumb || meta.img || ''
                };
                try { window.__play_meta = enriched; } catch {}
                try {
                  if (mounted) {
                    setBackPid(String(popupQ));
                    setBackKind(String(popup?.contentType || ''));
                  }
                } catch {}
                if (user) {
                  contApi.upsertSeries(user.uid, popupQ, {
                    playId: id,
                    path: `/play/${id}`,
                    epTitle: enriched.epTitle || '',
                    showTitle: enriched.showTitle || '',
                    img: enriched.img || '',
                    season: enriched.season || null,
                    index: enriched.index || null,
                  });
                }
              }
            } else {
              // popup not found -> orphan; delete page
              if (pageRecord && pageRecord.path) {
                try { await pagesApi.removePage(pageRecord.path); } catch {}
              }
            }
          }
        } catch {}
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [params.id, q]);

  // Fetch popup metadata when available (only applicable for series)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const pid = backPid || (window.__play_meta && window.__play_meta.popupId) || q.get('popup') || '';
        if (!pid) return;
        const r = await fetch(`/api/popups/${encodeURIComponent(pid)}`, { cache: 'no-store' });
        if (!r.ok) return;
        const j = await r.json();
        if (!alive) return;
        setPopup(j || null);
        setCurrentSeason(1);
      } catch (e) {}
    })();
    return () => { alive = false; };
  }, [backPid]);

  // Refresh popup metadata when admin edits (catalog/search)
  useEffect(() => {
    function onChanged(ev) {
      try {
        const detail = ev && ev.detail ? ev.detail : {};
        const pid = detail && detail.popupId != null ? String(detail.popupId) : null;
        if (!pid) return;
        if (!popup || String(popup.id) !== pid) return;
        (async () => {
          try {
            const r = await fetch(`/api/popups/${encodeURIComponent(pid)}`, { cache: 'no-store' });
            if (!r.ok) return;
            const j = await r.json();
            setPopup(j || null);
            // Try to update current user's continueSeries entries to match new popup ordering/ids
            try {
              if (user && user.uid) {
                const list = await contApi.listSeries(user.uid).catch(() => []);
                for (const cont of Array.isArray(list) ? list : []) {
                  try {
                    if (String(cont.popupId||'') !== String(pid)) continue;
                    // try to find episode in new popup by playId first
                    let matched = (j.episodes || []).find(e => String(e.playId||'') === String(cont.playId||''));
                    // fallback: match by season+index
                    if (!matched) matched = (j.episodes || []).find(e => Number(e.season || 1) === Number(cont.season || 1) && Number(e.index || 0) === Number(cont.index || 0));
                    // fallback: match by trimmed title
                    if (!matched) matched = (j.episodes || []).find(e => String((e.title||'')).trim() === String((cont.epTitle||'')).trim());
                    if (matched) {
                      const needUpdate = String(matched.playId||'') !== String(cont.playId||'') || Number(matched.index || 0) !== Number(cont.index || 0) || String((matched.title||'')) !== String((cont.epTitle||''));
                      if (needUpdate) {
                        await contApi.upsertSeries(user.uid, pid, {
                          playId: String(matched.playId||''),
                          path: `/play/${String(matched.playId||'')}`,
                          epTitle: matched.title || cont.epTitle || '',
                          showTitle: j.title || cont.showTitle || '',
                          img: matched.thumb || j.listThumb || cont.img || '',
                          season: matched.season || null,
                          index: matched.index || null,
                        }).catch(()=>{});
                      }
                    }
                  } catch {}
                }
                try { window.dispatchEvent(new CustomEvent('continue:changed', { detail: { popupId: pid } })); } catch {}
              }
            } catch {}
          } catch {}
        })();
      } catch {}
    }
    window.addEventListener('catalog:changed', onChanged);
    window.addEventListener('search:changed', onChanged);
    return () => { window.removeEventListener('catalog:changed', onChanged); window.removeEventListener('search:changed', onChanged); };
  }, [popup]);

  // Listen for admin-side popup updates/remaps and refresh immediately
  useEffect(() => {
    function onPopupEvent(ev) {
      try {
        const detail = ev && ev.detail ? ev.detail : {};
        // support multiple detail shapes: { id } or { popupId }
        const pid = detail && (detail.popupId != null ? String(detail.popupId) : (detail.id != null ? String(detail.id) : null));
        if (!pid) return;
        if (!popup || String(popup.id) !== pid) return;
        (async () => {
          try {
            const r = await fetch(`/api/popups/${encodeURIComponent(pid)}`, { cache: 'no-store' });
            if (!r.ok) return;
            const j = await r.json();
            setPopup(j || null);
            // if sidebar open, re-center current episode instantly after update
            if (sidebarOpen) {
              const idx = (j.episodes || []).findIndex(e => String(e.playId||'') === String(playId||''));
              if (idx >= 0) setTimeout(() => scrollToEpisodeIdx(idx, true), 40);
            }
          } catch {}
        })();
      } catch {}
    }
    const events = ['popup:remapped','popup:updated','popup:changed','popup:saved','catalog:changed'];
    events.forEach(evName => window.addEventListener(evName, onPopupEvent));
    return () => events.forEach(evName => window.removeEventListener(evName, onPopupEvent));
  }, [popup, sidebarOpen, playId]);

  // On mount: remember only the LAST watched page per series (no time tracking), as requested
  useEffect(() => {
    if (!user || !playId) return;
    try {
      const meta = window.__play_meta || {};
      const popupId = meta.popupId || q.get('popup') || null;
      if (!popupId) return;
      contApi.upsertSeries(user.uid, popupId, {
        playId,
        path: `/play/${playId}`,
        epTitle: meta.epTitle || '',
        showTitle: meta.showTitle || '',
        img: meta.img || '',
        season: meta.season || null,
        index: meta.index || null,
      });
    } catch {}
  }, [user?.uid, playId]);

  // cleanup hover timer on unmount
  useEffect(() => {
    return () => {
      if (hoverCloseTimer.current) {
        clearTimeout(hoverCloseTimer.current);
        hoverCloseTimer.current = null;
      }
      if (skipCloseTimer.current) {
        clearTimeout(skipCloseTimer.current);
        skipCloseTimer.current = null;
      }
    };
  }, []);

  // Integrate premium click handler similar to the jQuery/JWPlayer snippet
  useEffect(() => {
    let isRequestPending = false;
    let lastRequestTime = 0;
    const requestCooldown = 3000;

    function isHidden(el) {
      if (!el) return true;
      const style = window.getComputedStyle(el);
      return style.display === 'none' || style.visibility === 'hidden' || el.offsetParent === null;
    }

    function qsAll(sel) { return Array.from(document.querySelectorAll(sel)); }
    function showLoader(show) {
      const l = document.querySelector('.loader');
      if (!l) return;
      l.style.display = show ? '' : 'none';
    }
    function addAlert(html) {
      const target = document.querySelector('.playvideoPrm');
      if (!target) return;
      const el = document.createElement('div');
      el.className = 'alert-message error';
      el.innerHTML = html;
      target.parentNode && target.parentNode.insertBefore(el, target);
      return el;
    }

    function setupJW(url) {
      try {
        if (!window.jwplayer) return false;
        let host = document.getElementById('jwplayer');
        if (!host) {
          const wrapper = document.createElement('div');
          wrapper.style.position = 'fixed';
          wrapper.style.inset = '0';
          wrapper.style.background = '#000';
          wrapper.style.zIndex = '2147483000';
          const close = document.createElement('button');
          close.textContent = '×';
          close.setAttribute('aria-label', 'Zavřít');
          Object.assign(close.style, { position:'absolute', top:'10px', right:'10px', width:'36px', height:'36px', borderRadius:'50%', border:'1px solid rgba(255,255,255,0.35)', background:'rgba(0,0,0,0.45)', color:'#fff', cursor:'pointer', zIndex:'2' });
          close.onclick = () => document.body.removeChild(wrapper);
          host = document.createElement('div'); host.id = 'jwplayer'; host.style.width = '100%'; host.style.height = '100%';
          wrapper.appendChild(close);
          wrapper.appendChild(host);
          document.body.appendChild(wrapper);
        }
        const player = window.jwplayer('jwplayer').setup({
          sources: [{ file: url }],
          width: '100%', height: '100%', aspectratio: '16:9', stretching: 'uniform', preload: 'metadata', autostart: true,
          fullscreenOrientationLock: 'none', cast: {}, abouttext: 'FileMoon', aboutlink: 'https://filemoon.sx',
          playbackRateControls: true, playbackRates: [0.25, 0.50, 0.75, 1, 1.25, 1.5, 2],
        });
        player.on('ready', function () {
          try {
            window.jwplayer().addButton('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"/>', 'Forward 10 sec', function () { window.jwplayer().seek(window.jwplayer().getPosition() + 10); }, 'ff11');
            const fwd = document.querySelector('div[button=ff11]'); if (fwd) { const after = document.querySelector('.jw-icon-rewind'); after && after.parentNode && after.parentNode.insertBefore(fwd, after.nextSibling); }
            window.jwplayer().addButton('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"/>', 'Rewind 10 sec', function () { let tt = window.jwplayer().getPosition() - 10; if (tt < 0) tt = 0; window.jwplayer().seek(tt); }, 'ff00');
            const rwd = document.querySelector('div[button=ff00]'); if (rwd) { const after = document.querySelector('.jw-icon-rewind'); after && after.parentNode && after.parentNode.insertBefore(rwd, after.nextSibling); }
            const rw = document.querySelector('div.jw-icon-rewind'); if (rw) rw.style.display = 'none';
          } catch {}
        });
        return true;
      } catch { return false; }
    }

    async function handleClick(ev) {
      const target = ev.target.closest('.playvideoPrm');
      if (!target) return;
      // prevent other handlers from acting
      ev.preventDefault(); ev.stopPropagation();
      if (target.classList.contains('disabled') || isHidden(target)) return;
      const fileCode = target.getAttribute('data-filecode') || target.dataset.filecode || '';
      const now = Date.now();
      if (isRequestPending) return;
      if (now - lastRequestTime < requestCooldown) {
        qsAll('.playvideoPrm').forEach(el => { el.style.display = 'none'; el.classList.add('disabled'); });
        showLoader(true);
        setTimeout(() => {
          qsAll('.playvideoPrm').forEach(el => { el.style.display = ''; el.classList.remove('disabled'); });
          showLoader(false);
        }, requestCooldown);
        return;
      }
      isRequestPending = true; lastRequestTime = now;
      qsAll('.playvideoPrm').forEach(el => el.classList.add('disabled'));

      const ctrl = new AbortController();
      const to = setTimeout(() => ctrl.abort(), 1000);
      try {
        const res = await fetch('/sources/getlink', {
          method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({ fileCode }), signal: ctrl.signal,
        });
        clearTimeout(to);
        const response = await res.json().catch(() => ({ error: true, message: 'Bad JSON' }));
        if (response.error) {
          qsAll('.playvideoPrm').forEach(el => el.classList.remove('disabled'));
          addAlert(`Error: ${response.message || 'Unknown error'}`);
        } else {
          qsAll('.playvideoPrm').forEach(el => el.style.display = 'none');
          const btnNew = document.querySelector('.play_buttonNew'); if (btnNew) btnNew.style.display = 'none';
          qsAll('.alert-message').forEach(n => n.remove());
          const streamPremiumURL = response.url || '';
          let srv = '';
          try { const u = new URL(streamPremiumURL, window.location.href); srv = u.searchParams.get('srv') || ''; } catch {}
          if (!srv) {
            qsAll('.playvideoPrm').forEach(el => el.classList.add('disabled'));
            const msg = addAlert("Missing srv. The file is either transferring or the transfer has failed. <a class='show-iframe' href='#'>Show Embed</a>");
            if (msg) {
              const a = msg.querySelector('.show-iframe');
              if (a) a.addEventListener('click', (e) => {
                e.preventDefault();
                const iframe = document.createElement('iframe');
                iframe.className = 'iframe-videoTest';
                iframe.src = streamPremiumURL || '';
                iframe.setAttribute('scrolling','no');
                iframe.style.width = '100%'; iframe.style.height = '100%'; iframe.style.border = '0';
                const target = document.querySelector('.playvideoPrm');
                if (target && target.parentNode) target.parentNode.insertBefore(iframe, target);
                showLoader(false);
                document.body.classList.remove('premium');
              });
            }
          } else {
            const ok = setupJW(streamPremiumURL);
            if (!ok) {
              // fallback to simple iframe fullscreen overlay
              const wrap = document.createElement('div');
              Object.assign(wrap.style, { position:'fixed', inset:'0', background:'#000', zIndex:'2147483000' });
              const close = document.createElement('button');
              close.textContent = '×';
              Object.assign(close.style, { position:'absolute', top:'10px', right:'10px', width:'36px', height:'36px', borderRadius:'50%', border:'1px solid rgba(255,255,255,0.35)', background:'rgba(0,0,0,0.45)', color:'#fff', cursor:'pointer', zIndex:'2' });
              close.onclick = () => document.body.removeChild(wrap);
              const iframe = document.createElement('iframe');
              iframe.src = streamPremiumURL;
              iframe.allowFullscreen = true; iframe.style.width = '100%'; iframe.style.height = '100%'; iframe.style.border = '0';
              wrap.appendChild(close); wrap.appendChild(iframe); document.body.appendChild(wrap);
            }
          }
        }
      } catch (e) {
        qsAll('.playvideoPrm').forEach(el => el.classList.remove('disabled'));
        qsAll('.alert-message').forEach(n => n.remove());
        addAlert('Error occurred, refresh page or click on play button again');
      } finally {
        isRequestPending = false;
        const anyHidden = qsAll('.playvideoPrm').every(el => el.style.display === 'none');
        if (!anyHidden) setTimeout(() => qsAll('.playvideoPrm').forEach(el => el.classList.remove('disabled')), requestCooldown);
      }
    }

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, []);

  return (
    <div style={{ position:'fixed', inset:0, background:'#000', display:'flex', alignItems:'center', justifyContent:'center' }}>
      {/* Base opacity for header icons; they fade in on hover */}
      {/* Use a small transition so icons fade in/out smoothly */}
      {/* ICON_BASE_OPACITY used below for inline styles */}
      {/* eslint-disable-next-line no-unused-vars */}
      {null}
      
      {/* set a CSS variable fallback via inline style where needed */}
      {/* Scoped styles for the episode sidebar scrollbar (only affects the episode list) */}
      <style>{`
        /* Slightly wider gray scrollbar for the episode sidebar, hide arrow buttons */
        .play-episodes-sidebar { scrollbar-width: auto; scrollbar-color: rgba(136,136,136,0.75) transparent; scrollbar-gutter: stable; }
        .play-episodes-sidebar::-webkit-scrollbar { width: 14px; height: 14px; }
        .play-episodes-sidebar::-webkit-scrollbar-track { background: transparent; }
        .play-episodes-sidebar::-webkit-scrollbar-thumb { background: rgba(136,136,136,0.75); border-radius: 8px; border: 3px solid transparent; }
        /* Hide the up/down arrow buttons in WebKit browsers (cover multiple pseudo-element variations) */
        .play-episodes-sidebar::-webkit-scrollbar-button,
        .play-episodes-sidebar::-webkit-scrollbar-button:single-button,
        .play-episodes-sidebar::-webkit-scrollbar-button:start,
        .play-episodes-sidebar::-webkit-scrollbar-button:end,
        .play-episodes-sidebar::-webkit-scrollbar-button:vertical,
        .play-episodes-sidebar::-webkit-scrollbar-button:vertical:decrement,
        .play-episodes-sidebar::-webkit-scrollbar-button:vertical:increment,
        .play-episodes-sidebar::-webkit-scrollbar-button:single-button:vertical {
          display: none; width: 0; height: 0; }
        /* extra catch-all for WebKit scrollbar buttons */
        .play-episodes-sidebar::-webkit-scrollbar-button { display: none; }
        .play-episodes-sidebar::-webkit-scrollbar-corner { background: transparent; }
      /* When the sidebar is fading out, temporarily suppress child transitions
        and animations so content doesn't visibly reflow while opacity changes. */
      .play-episodes-sidebar.fading * { transition: none !important; animation: none !important; }
      `}</style>
      {vipBlocked && (
        <div style={{ position:'absolute', inset:0, zIndex:30, display:'flex', alignItems:'center', justifyContent:'center', pointerEvents:'auto' }}>
          <div style={{ background:'rgba(0,0,0,0.9)', color:'#fff', padding:24, borderRadius:10, maxWidth:640, textAlign:'center', boxShadow:'0 8px 30px rgba(0,0,0,0.6)' }}>
            <h2 style={{ margin:0, marginBottom:8 }}>Přístup omezen</h2>
            <div style={{ marginBottom:14 }}>{blockedReason || 'Tato epizoda je pouze pro VIP uživatele.'}</div>
            <div style={{ display:'flex', gap:10, justifyContent:'center' }}>
              <button onClick={() => { if (!user) window.location.assign('/profile'); else window.history.back(); }} style={{ padding:'8px 12px', background:'#2d8a34', color:'#fff', border:'none', borderRadius:6 }}>Přihlásit / Upgradovat</button>
              <button onClick={() => { window.history.back(); }} style={{ padding:'8px 12px', background:'#444', color:'#fff', border:'none', borderRadius:6 }}>Zpět</button>
            </div>
          </div>
        </div>
      )}
      <div style={{ position:'absolute', top: headerOffset, left:12, zIndex:5, display:'flex', alignItems:'center', gap:8 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center' }}>
          <a
          data-skip-preview-interceptor
          href={(() => {
            const base = backKind === 'film' ? '/filmy' : backKind === 'serial' ? '/serialy' : '/';
            return backPid ? `${base}?preview=${encodeURIComponent(backPid)}` : base;
          })()}
          onClick={async (e) => {
            e.preventDefault();
            try {
              // derive matched episode from current popup + playId in render scope
              if (popup && playId) {
                const j = popup;
                const matched = (j.episodes || []).find(ep => String(ep.playId||'') === String(playId||''));
                if (matched && user && user.uid) {
                  const epsOfSeason = (j.episodes || []).filter(x => Number(x.season || 1) === Number(matched.season || 1));
                  const idxWithinSeason = Math.max(0, epsOfSeason.indexOf(matched)) + 1;
                  await contApi.upsertSeries(user.uid, String(j.id||''), {
                    playId: String(matched.playId||''),
                    path: `/play/${String(matched.playId||'')}`,
                    epTitle: matched.title || '',
                    showTitle: j.title || '',
                    img: matched.thumb || j.listThumb || '',
                    season: matched.season || null,
                    index: idxWithinSeason || null,
                  }).catch(()=>{});
                }
              }
            } catch (err) {
              // swallow - not critical for navigation
            }
            if (window.history.length > 1) {
              window.history.back();
            } else {
              window.location.assign('/');
            }
          }}
          style={{
            display:'inline-flex', alignItems:'center', justifyContent:'center',
            width:48, height:48, borderRadius:24, background:'rgba(0,0,0,0.35)',
            color:'#fff', textDecoration:'none', cursor:'pointer',
            opacity: backHover ? 1 : 0.6,
            transition: 'opacity 180ms ease'
          }}
          onMouseEnter={() => setBackHover(true)}
          onMouseLeave={() => setBackHover(false)}
        >
          <IoIosArrowBack size={28} color="#fff" />
          </a>

            {/* Subtitles / language selector icon (stacked under back button) */}
            <div
              ref={langIconRef}
              onMouseEnter={() => {
                if (langCloseTimer.current) { clearTimeout(langCloseTimer.current); langCloseTimer.current = null; }
                // compute popup position relative to icon
                try {
                  const r = langIconRef.current && langIconRef.current.getBoundingClientRect();
                  if (r) {
                    const left = Math.round(r.right + 8);
                    const top = Math.max(8, Math.round(r.top - 8));
                    setLangPopupStyle({ position: 'fixed', left: `${left}px`, top: `${top}px` });
                  }
                } catch (e) {}
                setLangPopupOpen(true);
              }}
              onMouseLeave={() => {
                if (langCloseTimer.current) clearTimeout(langCloseTimer.current);
                langCloseTimer.current = setTimeout(() => setLangPopupOpen(false), 140);
              }}
              style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:40, height:36, borderRadius:8, background:'rgba(0,0,0,0.35)', cursor:'pointer', opacity: langPopupOpen ? 1 : 0.6, transition: 'opacity 180ms ease' }}
            >
              <MdOutlineSubtitles size={20} color="#fff" />
              {/* Popup (positioned and animated) */}
              <div
                onMouseEnter={() => { if (langCloseTimer.current) { clearTimeout(langCloseTimer.current); langCloseTimer.current = null; } setLangPopupOpen(true); }}
                onMouseLeave={() => { if (langCloseTimer.current) clearTimeout(langCloseTimer.current); langCloseTimer.current = setTimeout(() => setLangPopupOpen(false), 140); }}
                style={{
                  ...(langPopupStyle || {}),
                  zIndex: 120,
                  background: '#071014',
                  color: '#fff',
                  padding: 12,
                  borderRadius: 8,
                  boxShadow: '0 12px 36px rgba(0,0,0,0.6)',
                  minWidth: 180,
                  pointerEvents: langPopupOpen ? 'auto' : 'none',
                  opacity: langPopupOpen ? 1 : 0,
                  transform: langPopupOpen ? 'translateY(0)' : 'translateY(-6px)',
                  transition: 'opacity 160ms ease, transform 160ms ease',
                  position: 'fixed'
                }}
              >
                <div style={{ fontWeight:800, marginBottom:8 }}>Jazyky:</div>
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {/* default Czech / main video */}
                  {currentEp && currentEp.videoUrl && (() => {
                    let activeMain = false;
                    try { activeMain = toEmbedAutoplay(currentEp.videoUrl) === (url || ''); } catch(e) { activeMain = false; }
                    const mainCode = '__main__';
                    const base = { display:'flex', gap:8, alignItems:'center', padding:8, borderRadius:6, border:'1px solid rgba(255,255,255,0.04)', color:'#fff', background:'transparent', cursor:'pointer' };
                    const activeBg = { background: 'rgba(255,255,255,0.10)' };
                    const hoverBg = { background: 'rgba(255,255,255,0.04)' };
                    const style = Object.assign({}, base, (activeMain ? activeBg : (hoveredLangCode === mainCode ? hoverBg : {})));
                    return (
                      <button
                        onClick={() => switchToLanguageVideo(currentEp.videoUrl)}
                        onMouseEnter={() => setHoveredLangCode(mainCode)}
                        onMouseLeave={() => setHoveredLangCode(null)}
                        style={style}
                      >
                        <ReactCountryFlag svg countryCode={'CZ'} style={{ width:22, height:14, display:'inline-block' }} title={'Čeština'} />
                        <span>Čeština</span>
                        {activeMain ? <IoMdCheckmark size={16} color="#fff" style={{ marginLeft:8 }} /> : null}
                      </button>
                    );
                  })()}

                  {(Array.isArray(currentEp?.languages) ? currentEp.languages : []).map(l => {
                    const code = l.code || (l.lang || l.locale) || String(l.name||'');
                    let active = false;
                    try { active = toEmbedAutoplay(l.videoUrl || '') === (url || ''); } catch (e) { active = false; }
                    const base = { display:'flex', gap:8, alignItems:'center', padding:8, borderRadius:6, border:'1px solid rgba(255,255,255,0.04)', color:'#fff', background:'transparent', cursor:'pointer', justifyContent: 'space-between' };
                    const leftPart = { display:'flex', gap:8, alignItems:'center' };
                    const activeBg = { background: 'rgba(255,255,255,0.10)' };
                    const hoverBg = { background: 'rgba(255,255,255,0.04)' };
                    const style = Object.assign({}, base, (active ? activeBg : (hoveredLangCode === code ? hoverBg : {})));
                    return (
                      <button
                        key={code}
                        onClick={() => switchToLanguageVideo(l.videoUrl)}
                        onMouseEnter={() => setHoveredLangCode(code)}
                        onMouseLeave={() => setHoveredLangCode(null)}
                        style={style}
                      >
                        <div style={leftPart}>
                          {(() => {
                            const raw = l.flag || (l.country) || 'gb';
                            if (raw && (raw.indexOf('/')>=0 || raw.indexOf('.')>=0)) return <img src={raw} alt={code} style={{ width:22, height:14 }} />;
                            return <ReactCountryFlag svg countryCode={String(raw||'gb').toUpperCase()} style={{ width:22, height:14, display:'inline-block' }} title={l.name || l.code} />;
                          })()}
                          <span>{l.name || l.code}</span>
                        </div>
                        {active ? <IoMdCheckmark size={16} color="#fff" style={{ marginLeft:8 }} /> : null}
                      </button>
                    );
                  })}
                  {(!currentEp || (!currentEp.videoUrl && (!currentEp.languages || !currentEp.languages.length))) && (
                    <div style={{ color:'#bbb' }}>Žádné alternativní jazyky.</div>
                  )}
                </div>
              </div>
            </div>
        </div>
      </div>
      <div style={{ position:'absolute', top: headerOffset, right:12, zIndex:5, display:'flex', flexDirection:'column', alignItems:'flex-end', gap:8 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <a
            href="#nahlasit"
            onClick={(e) => { e.preventDefault(); setIsReportOpen(true); }}
            style={{
              display:'inline-flex', alignItems:'center', justifyContent:'center',
              width:48, height:48, borderRadius:24, background:'rgba(0,0,0,0.35)',
              color:'#fff', textDecoration:'none', cursor:'pointer',
              opacity: reportHover ? 1 : 0.6,
              transition: 'opacity 180ms ease'
            }}
            onMouseEnter={() => setReportHover(true)}
            onMouseLeave={() => setReportHover(false)}
          >
            <MdOutlineFlag size={22} color="#fff" />
          </a>
        </div>
        {/* Series sidebar toggle - only show for series popups */}
        {popup && String(popup.contentType) === 'serial' ? (
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <div style={{ position:'relative', display:'inline-flex', alignItems:'center' }}>
              <button
                onClick={(e) => { e.preventDefault(); /* click disabled - hover-only */ }}
                title=""
                style={{
                  display:'inline-flex', alignItems:'center', justifyContent:'center',
                  width:48, height:48, borderRadius:24, background:'rgba(0,0,0,0.35)',
                  color:'#fff', textDecoration:'none', cursor:'pointer', border: 'none',
                  opacity: episodesHover ? 1 : 0.6,
                  transition: 'opacity 180ms ease'
                }}
                onMouseEnter={() => { setEpisodesHover(true); if (hoverCloseTimer.current) { clearTimeout(hoverCloseTimer.current); hoverCloseTimer.current = null; } setSidebarOpen(true); }}
                onMouseLeave={() => { setEpisodesHover(false); if (hoverCloseTimer.current) clearTimeout(hoverCloseTimer.current); hoverCloseTimer.current = setTimeout(() => { setSidebarOpen(false); }, 260); }}
              >
                <PiCardsLight size={22} color="#fff" />
              </button>

              {/* Skip-next icon + popup positioned under the episodes icon (anchored, won't shift on hover) */}
              {nextEp ? (
                <div style={{ position:'absolute', top: 'calc(100% + 8px)', right: 0, zIndex:2205 }}>
                  <button
                    onMouseEnter={() => { if (skipCloseTimer.current) { clearTimeout(skipCloseTimer.current); skipCloseTimer.current = null; } setSkipHover(true); }}
                    onMouseLeave={() => { if (skipCloseTimer.current) clearTimeout(skipCloseTimer.current); skipCloseTimer.current = setTimeout(() => setSkipHover(false), 120); }}
                    onClick={(e) => { e.preventDefault(); try { if (nextEp && nextEp.ep && nextEp.ep.playId) window.location.assign(`/play/${nextEp.ep.playId}`); } catch {} }}
                    style={{ width:48, height:48, borderRadius:24, background:'rgba(0,0,0,0.35)', display:'inline-flex', alignItems:'center', justifyContent:'center', border:'none', color:'#fff', cursor: 'pointer', opacity: skipHover ? 1 : 0.6, transition: 'opacity 180ms ease' }}
                    aria-label='Přeskočit na další díl'
                  >
                    <TbPlayerSkipForward size={22} color='#fff' />
                  </button>

                  <div
                    onMouseEnter={() => { if (skipCloseTimer.current) { clearTimeout(skipCloseTimer.current); skipCloseTimer.current = null; } /* do not open popup from panel hover */ }}
                    onMouseLeave={() => { if (skipCloseTimer.current) clearTimeout(skipCloseTimer.current); skipCloseTimer.current = setTimeout(() => setSkipHover(false), 120); }}
                    style={{ position:'absolute', right: 56, top: -6, width:420, zIndex:2205 }}>
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => { try { if (nextEp && nextEp.ep && nextEp.ep.playId) window.location.assign(`/play/${nextEp.ep.playId}`); } catch {} }}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { try { if (nextEp && nextEp.ep && nextEp.ep.playId) window.location.assign(`/play/${nextEp.ep.playId}`); } catch {} } }}
                      style={{
                      width: '100%',
                      background: '#111',
                      color: '#fff',
                      borderRadius: 10,
                      padding: 12,
                      boxShadow: '0 8px 30px rgba(0,0,0,0.55)',
                      opacity: skipHover ? 1 : 0,
                      transition: 'opacity 180ms ease',
                      cursor: nextEp && nextEp.ep && nextEp.ep.playId ? 'pointer' : 'default',
                      willChange: 'opacity',
                      pointerEvents: skipHover ? 'auto' : 'none'
                    }}>
                      <div style={{ display:'flex', flexDirection:'column', gap:10, width:'100%' }}>
                        <div style={{ fontSize:18, fontWeight:600, textTransform:'none' }}>Další díl</div>
                        <div style={{ display:'flex', gap:14, alignItems:'center' }}>
                          <div
                            onClick={() => { try { if (nextEp && nextEp.ep && nextEp.ep.playId) window.location.assign(`/play/${nextEp.ep.playId}`); } catch {} }}
                            style={{ position:'relative', width:160, height:96, borderRadius:8, overflow:'hidden', background:'#000', flexShrink:0, cursor: nextEp && nextEp.ep && nextEp.ep.playId ? 'pointer' : 'default' }}
                          >
                            <img src={(nextEp && nextEp.ep && (nextEp.ep.thumb || popup.listThumb)) || '/images/placeholder-thumb.png'} alt={nextEp && nextEp.ep && nextEp.ep.title ? nextEp.ep.title : 'Další epizoda'} style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} />
                            <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', pointerEvents:'auto' }}>
                              <div
                                onMouseEnter={() => setSkipPlayHover(true)}
                                onMouseLeave={() => setSkipPlayHover(false)}
                                onClick={(e) => { e.stopPropagation(); try { if (nextEp && nextEp.ep && nextEp.ep.playId) window.location.assign(`/play/${nextEp.ep.playId}`); } catch {} }}
                                style={{ width:52, height:52, borderRadius:26, background: skipPlayHover ? 'rgba(255,255,255,0.22)' : 'rgba(255,255,255,0.14)', display:'flex', alignItems:'center', justifyContent:'center', transform: skipPlayHover ? 'translateZ(0) scale(1.22)' : 'translateZ(0) scale(1)', transformOrigin: 'center', willChange: 'transform, background', backfaceVisibility: 'hidden', transition:'transform .2s ease, background .2s ease', cursor: nextEp && nextEp.ep && nextEp.ep.playId ? 'pointer' : 'default' }}
                              >
                                <FaPlay color='#fff' />
                              </div>
                            </div>
                          </div>

                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ display:'flex', alignItems:'center', gap:24 }}>
                              <div style={{ fontWeight:700, fontSize:17, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                                {nextEp && nextEp.ep ? `${typeof nextEp.episodeNumberInSeason === 'number' ? `${nextEp.episodeNumberInSeason}. ` : ''}${nextEp.ep.title || 'Další epizoda'}` : ''}
                              </div>
                            </div>
                            <div style={{ fontSize:13, opacity:0.9, marginTop:8, maxHeight:54, overflow:'hidden', display:'-webkit-box', WebkitLineClamp:3, WebkitBoxOrient:'vertical' }}>{nextEp && nextEp.ep && nextEp.ep.desc ? nextEp.ep.desc : ''}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
      {loading ? (
        <div style={{ color:'#fff' }}>Načítám…</div>
      ) : url ? (
        <iframe
          src={url}
          title="Přehrávač"
          style={{ position:'absolute', inset:0, width:'100%', height:'100%', border:'none' }}
          allow="autoplay; fullscreen; picture-in-picture; encrypted-media; accelerometer; clipboard-write"
          allowFullScreen
          referrerPolicy="no-referrer-when-downgrade"
        />
      ) : (
        <div style={{ color:'#fff' }}>Video nenalezeno.</div>
      )}
      <ReportIssueModal isOpen={isReportOpen} onClose={() => setIsReportOpen(false)} />
      {/* Sidebar for series navigation */}
      {popup && String(popup.contentType) === 'serial' && (
        <div
          className={`play-episodes-sidebar ${sidebarFading ? 'fading' : ''}`}
          onMouseEnter={() => { if (hoverCloseTimer.current) { clearTimeout(hoverCloseTimer.current); hoverCloseTimer.current = null; } }}
          onMouseLeave={() => { if (hoverCloseTimer.current) clearTimeout(hoverCloseTimer.current); hoverCloseTimer.current = setTimeout(() => { setShowSeasonsPanel(false); setSidebarOpen(false); }, 220); }}
          style={{ boxSizing:'border-box', position:'fixed', top:0, right: '64px', width:SIDEBAR_WIDTH, height:'100%', background:'#06080a', color:'#fff', zIndex:2150, transition:'opacity 120ms ease, transform 160ms ease', opacity: (sidebarOpen && sidebarReady) ? 1 : 0, pointerEvents: (sidebarOpen && sidebarReady) ? 'auto' : 'none', overflowY:'auto', padding:16, paddingRight:32, paddingBottom:16 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12 }}>
            <div style={{ fontWeight:700, fontSize:20 }}>{popup.title || 'Série'}</div>
          </div>
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {/**
             * Show only the first season initially. When the user expands an episode
             * from season 1, reveal all seasons stacked below. The episodes remain
             * listed under each season as before.
             */}
            {(() => {
              const totalSeasons = Math.max(1, Number(popup.seasons) || 1);
              // If we're in seasons-only mode, render only seasons as a separate view
              if (seasonsViewOnly) {
                return (
                  <div style={{ paddingTop:8, transition:'transform 220ms ease, opacity 180ms ease', transform: seasonsFadeIn ? 'translateX(8px)' : 'translateX(0)', opacity: seasonsFadeIn ? 0 : 1 }}>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
                      <div style={{ display: 'none' }} />
                    </div>
                    <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                      {Array.from({ length: totalSeasons }).map((_, si) => (
                        <div
                          key={si}
                          ref={el => { if (el) seasonRefs.current[si] = el; else delete seasonRefs.current[si]; }}
                          role="button"
                          tabIndex={0}
                          onMouseEnter={() => { try { setHighlightedSeason(si+1); } catch {} }}
                          onMouseLeave={() => { try { setHighlightedSeason(null); } catch {} }}
                                onClick={() => {
                                  // If the season has multiple episodes, navigate to the first one.
                                  // If it has only one episode, open the season view instead (so user sees the season popup).
                                  try {
                                    const eps = Array.isArray(popup.episodes) ? popup.episodes.filter(e => Number(e.season || 1) === (si+1)) : [];
                                    const first = eps && eps.length ? eps[0] : null;
                                    // Always open the season view instead of auto-navigating to a single episode
                                  } catch {}
                                  // Open sidebar and select season (immediately update visible season to avoid flash)
                                  setCurrentSeason(si+1);
                                  try { setDisplaySeason(si+1); } catch {}
                                  setSeasonEnteringIdx(si);
                                  setSeasonsViewOnly(false); setRevealedAllSeasons(false); setSidebarOpen(true); setExpandedEpIdx(null);
                                  try {
                                      const first = (Array.isArray(popup.episodes) ? popup.episodes.find(e => Number(e.season || 1) === (si+1)) : null);
                                      const gi = first ? (popup.episodes || []).indexOf(first) : -1;
                                      if (gi >= 0) {
                                        setTimeout(() => {
                                          try { scrollToEpisodeIdx(gi, true); } catch {}
                                          try {
                                            const pos = mousePos.current || {};
                                            const elAt = (typeof pos.x === 'number') ? document.elementFromPoint(pos.x, pos.y) : null;
                                            const epEl = episodeRefs.current && episodeRefs.current[gi];
                                            if (epEl && elAt && (epEl === elAt || epEl.contains(elAt) || (elAt.contains && elAt.contains(epEl)))) {
                                              // pointer is already over the episode — activate hover state
                                              setHoveredEpIdx(gi);
                                            } else {
                                              // simulate a short hover so UI shows controls; clear shortly if pointer isn't actually over it
                                              setHoveredEpIdx(gi);
                                              setTimeout(() => {
                                                try {
                                                  const pos2 = mousePos.current || {};
                                                  const elAt2 = (typeof pos2.x === 'number') ? document.elementFromPoint(pos2.x, pos2.y) : null;
                                                  if (!(epEl && elAt2 && (epEl === elAt2 || epEl.contains(elAt2)))) {
                                                    setHoveredEpIdx(prev => prev === gi ? null : prev);
                                                  }
                                                } catch {}
                                              }, 200);
                                            }
                                          } catch {}
                                        }, 120);
                                      }
                                  } catch {}
                              }}
                          style={{ padding:'10px', cursor:'pointer', borderRadius:8, background: (highlightedSeason === si+1 ? 'rgba(255,255,255,0.08)' : 'transparent'), fontSize:20, fontWeight:700 }}
                        >
                          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', width:'100%' }}>
                            <div style={{ fontWeight:700, fontSize:20 }}>{si+1}. Řada</div>
                            {highlightedSeason === si+1 ? <FaArrowRightLong color='#fff' /> : null}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }

              const renderSeasonIndices = revealedAllSeasons ? Array.from({ length: totalSeasons }).map((_, i) => i) : [Math.max(0, (displaySeason || 1) - 1)];

              return renderSeasonIndices.map((si) => (
                <div key={si} style={{ borderTop: '1px solid rgba(255,255,255,0.04)', paddingTop:8, transition:'transform 220ms ease, opacity 180ms ease', transform: seasonsViewOnly ? 'translateX(12px)' : 'translateX(0)', opacity: seasonsViewOnly ? 0.15 : 1 }}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <button
                        onClick={() => {
                          // play a quick exit animation, then switch to seasons-only view
                          try { setSeasonExitingIdx(si); } catch {}
                          setTimeout(() => { try { setSeasonsViewOnly(true); } catch {} try { setSeasonExitingIdx(null); } catch {} }, 160);
                        }}
                        style={{ background:'transparent', border:'none', color:'#fff', cursor:'pointer', display:'inline-flex', alignItems:'center', justifyContent:'center', width:36, height:36, transform:'translateY(2px)' }} aria-label='Vybrat řadu'>
                        <FaArrowLeftLong size={18} color="#fff" />
                      </button>
                      <div style={{ fontWeight:800, fontSize:20 }}>{si+1}. Řada</div>
                    </div>
                    {highlightedSeason === si+1 ? <FaArrowRightLong color='#fff' /> : null}
                    {/* right-side season action removed per UX request */}
                  </div>
                      <div style={{
                        marginTop:8,
                        display: ((displaySeason != null ? displaySeason : currentSeason) === (si+1)) ? 'block' : 'none',
                        opacity: (seasonEnteringIdx === si || seasonExitingIdx === si) ? 0 : 1,
                        transform: (seasonEnteringIdx === si ? 'translateX(12px)' : (seasonExitingIdx === si ? 'translateX(12px)' : 'translateX(0)')),
                        transition: 'opacity 180ms ease, transform 180ms ease'
                      }}>
                    {(Array.isArray(popup.episodes) ? popup.episodes.filter(e => Number(e.season || 1) === (si+1) ) : []).map((ep, eidx) => {
                      const globalIdx = (popup.episodes || []).indexOf(ep);
                      // display position = position in the filtered season list (1-based)
                      const displayIndex = (eidx + 1);
                      const isActive = String(ep.playId||'') === String(playId);
                      const isExpanded = expandedEpIdx === globalIdx;
                      return (
                        <div
                          key={globalIdx}
                          ref={el => { try { if (el) episodeRefs.current[globalIdx] = el; else delete episodeRefs.current[globalIdx]; } catch {} }}
                          style={{
                            display:'flex', flexDirection:'column', gap:8, padding:14,
                            background: hoveredEpIdx === globalIdx ? 'rgba(255,255,255,0.08)' : (isActive ? 'rgba(255,255,255,0.06)' : 'transparent'),
                            borderRadius:10, marginBottom:10
                          }}
                          onMouseEnter={() => { if (!isActive) setHoveredEpIdx(globalIdx); }}
                          onMouseLeave={() => setHoveredEpIdx(prev => prev === globalIdx ? null : prev)}
                        >
                          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
                            <div style={{ display:'flex', alignItems:'center', gap:12, cursor: isActive ? 'default' : (ep.playId ? 'pointer' : 'default') }} onClick={() => { 
                              // If this is the currently playing episode, don't navigate
                              if (isActive) return;
                              // navigate to episode play page
                              if (ep.playId) { window.location.assign(`/play/${ep.playId}`); return; }
                              const wasCollapsed = expandedEpIdx == null;
                              setExpandedEpIdx(prev => prev === globalIdx ? null : globalIdx);
                              // Only reveal all seasons automatically when expanding the very first episode of season 1
                              if (wasCollapsed && Number(ep.season || 1) === 1 && Number(ep.index || 1) === 1) {
                                setRevealedAllSeasons(true);
                              }
                            }}>
                              <div style={{ position:'relative', width:160, height:92, borderRadius:8, overflow:'hidden', background:'#111', flexShrink:0 }}>
                                <img src={ep.thumb || popup.listThumb || '/images/placeholder-thumb.png'} alt={ep.title||''} style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} />
                                { /* show play overlay only when hovered and not active; animate play button on its own hover */ }
                                {(!isActive && hoveredEpIdx === globalIdx) ? (
                                  <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', pointerEvents:'auto' }}>
                                    <div
                                      onMouseEnter={() => setPlayIconHoverIdx(globalIdx)}
                                      onMouseLeave={() => setPlayIconHoverIdx(prev => prev === globalIdx ? null : prev)}
                                      onClick={(e) => { e.stopPropagation(); if (isActive) return; if (ep.playId) { window.location.assign(`/play/${ep.playId}`); } }}
                                      style={{
                                        width:48, height:48, borderRadius:24,
                                        background: playIconHoverIdx === globalIdx ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.12)',
                                        display:'flex', alignItems:'center', justifyContent:'center',
                                        transition:'transform .18s ease, background .18s ease, filter .18s ease',
                                        transform: playIconHoverIdx === globalIdx ? 'translateZ(0) scale(1.12)' : 'translateZ(0) scale(1)',
                                        transformOrigin: 'center',
                                        willChange: 'transform, filter',
                                        backfaceVisibility: 'hidden',
                                        filter: playIconHoverIdx === globalIdx ? 'brightness(1.15)' : 'brightness(1)',
                                        cursor: ep.playId ? 'pointer' : 'default'
                                      }}
                                    >
                                      <FaPlay color='#fff' />
                                    </div>
                                  </div>
                                ) : null}
                              </div>
                              <div style={{ flex:1, minWidth:0 }}>
                                <div style={{ position:'relative' }}>
                                  <div style={{ fontWeight:800, fontSize:16, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{(displayIndex != null ? `${displayIndex}. ` : '')}{ep.title || `Epizoda ${globalIdx+1}`}</div>
                                  {/* removed fade overlay per UX - prevents black block */}
                                </div>
                                <div style={{ position:'relative', fontSize:14, opacity:0.95, marginTop:6, maxHeight:40, overflow:'hidden', textOverflow:'ellipsis' }}>
                                  <div style={{ whiteSpace:'normal', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden' }}>{ep.desc || ''}</div>
                                  {/* removed fade overlay per UX - prevents black block */}
                                </div>
                              </div>
                            </div>
                            <div style={{ display:'flex', gap:8 }}>
                              {/* reserved for future per-episode controls */}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ));
            })()}
          </div>
        </div>
      )}
      
      {showSeasonPicker && popup && (
        <div style={{ position:'fixed', top: headerOffset + 60, left: 24, width:240, maxHeight:'60vh', overflowY:'auto', background:'#111', color:'#fff', borderRadius:8, padding:12, zIndex:2200 }}>
          <div style={{ fontWeight:800, marginBottom:8 }}>Vyber řadu</div>
          {Array.from({ length: Math.max(1, Number(popup.seasons) || 1) }).map((_, si) => (
            <div key={si} ref={el => { if (el) seasonRefs.current[`picker_${si}`] = el; else delete seasonRefs.current[`picker_${si}`]; }} style={{ padding:'8px 6px', cursor:'pointer', background: (highlightedSeason === (si+1) ? 'rgba(255,255,255,0.04)' : 'transparent'), borderRadius:6, marginBottom:6, fontSize:18 }} onMouseEnter={() => { try { setHighlightedSeason(si+1); } catch {} }} onMouseLeave={() => { try { setHighlightedSeason(null); } catch {} }} onClick={() => { setCurrentSeason(si+1); try { setDisplaySeason(si+1); } catch {} setShowSeasonPicker(false); }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', width:'100%' }}>
                <div>{si+1}. Řada</div>
                {highlightedSeason === (si+1) ? <FaArrowRightLong color='#fff' /> : null}
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Sliding seasons panel that hovers left inside the sidebar */}
      {popup && showSeasonsPanel && (
        <div
          onMouseEnter={() => { if (hoverCloseTimer.current) { clearTimeout(hoverCloseTimer.current); hoverCloseTimer.current = null; } }}
          onMouseLeave={() => { if (hoverCloseTimer.current) clearTimeout(hoverCloseTimer.current); hoverCloseTimer.current = setTimeout(() => setShowSeasonsPanel(false), 220); }}
      style={{ position:'fixed', top:0, left: sidebarOpen ? SIDEBAR_WIDTH : 0, width:SEASONS_PANEL_WIDTH, height:'100%', background:'#0b0b0c', color:'#fff', zIndex:2160, transition:'transform 200ms ease', transform: sidebarOpen ? `translateX(-${SEASONS_PANEL_WIDTH}px)` : 'translateX(0)' }}>
          <div style={{ padding:12 }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
              <div style={{ display: 'none' }} />
              <button onClick={() => setShowSeasonsPanel(false)} style={{ background:'transparent', border:'none', color:'#fff', cursor:'pointer' }}>Zavřít</button>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {Array.from({ length: Math.max(1, Number(popup.seasons) || 1) }).map((_, si) => (
                <div
                  key={si}
                  ref={el => { if (el) seasonRefs.current[`slide_${si}`] = el; else delete seasonRefs.current[`slide_${si}`]; }}
                  style={{ padding:'8px', cursor:'pointer', borderRadius:6, background: (highlightedSeason === si+1 ? 'rgba(255,255,255,0.04)' : 'transparent'), fontSize:18 }}
                  onMouseEnter={() => { /* preview episodes for this season */ setCurrentSeason(si+1); try { setDisplaySeason(si+1); } catch {} try { setHighlightedSeason(si+1); } catch {} }}
                  onMouseLeave={() => { try { setHighlightedSeason(null); } catch {} }}
                      onClick={() => {
                        /* select season and close panel, do NOT navigate */
                        setCurrentSeason(si+1); try { setDisplaySeason(si+1); } catch {} setSeasonEnteringIdx(si); setShowSeasonsPanel(false);
                        try {
                          const first = (Array.isArray(popup.episodes) ? popup.episodes.find(e => Number(e.season || 1) === (si+1)) : null);
                          const gi = first ? (popup.episodes || []).indexOf(first) : -1;
                          if (gi >= 0) {
                            setTimeout(() => {
                              try { scrollToEpisodeIdx(gi, true); } catch {}
                              try {
                                const pos = mousePos.current || {};
                                const elAt = (typeof pos.x === 'number') ? document.elementFromPoint(pos.x, pos.y) : null;
                                const epEl = episodeRefs.current && episodeRefs.current[gi];
                                if (epEl && elAt && (epEl === elAt || epEl.contains(elAt) || (elAt.contains && elAt.contains(epEl)))) {
                                  setHoveredEpIdx(gi);
                                } else {
                                  setHoveredEpIdx(gi);
                                  setTimeout(() => {
                                    try {
                                      const pos2 = mousePos.current || {};
                                      const elAt2 = (typeof pos2.x === 'number') ? document.elementFromPoint(pos2.x, pos2.y) : null;
                                      if (!(epEl && elAt2 && (epEl === elAt2 || epEl.contains(elAt2)))) {
                                        setHoveredEpIdx(prev => prev === gi ? null : prev);
                                      }
                                    } catch {}
                                  }, 200);
                                }
                              } catch {}
                            }, 80);
                          }
                        } catch {}
                      }}
                >
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', width:'100%' }}>
                    <div>{si+1}. Řada</div>
                    {highlightedSeason === si+1 ? <FaArrowRightLong color='#fff' /> : null}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
